import axios from 'axios';

// Создаем экземпляр axios с базовым URL
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add Bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 401 (unauthorized)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Флаг для использования моковых данных вместо реальных API-запросов
const USE_MOCK_DATA = false;

// Моковые данные для разработки
const MOCK_DATA = {
  users: Array.from({ length: 20 }, (_, i) => {
    const createdDate = new Date(Date.now() - Math.random() * 10000000000);
    const ageInHours = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60));
    const months = Math.floor(ageInHours / (24 * 30));
    const days = Math.floor((ageInHours - months * 24 * 30) / 24);
    const hours = ageInHours - months * 24 * 30 - days * 24;
    
    return {
      id: i + 1,
      firstName: `Имя${i + 1}`,
      lastName: `Фамилия${i + 1}`,
      secondName: `Отчество${i + 1}`,
      email: `user${i + 1}@example.com`,
      phone: `+7${900000000 + i}`,
      status: ['active', 'suspended', 'blocked'][i % 3],
      kycStatus: ['pending', 'approved', 'rejected'][i % 3],
      accountAge: `${months} мес. ${days} дней ${hours} часов`,
      spotAccountTurnoverDaily: Math.round(Math.random() * 10000),
      spotAccountTurnoverMonthly: Math.round(Math.random() * 100000),
      marginAccountTurnoverDaily: Math.round(Math.random() * 5000),
      marginAccountTurnoverMonthly: Math.round(Math.random() * 50000),
      createdAt: createdDate.toISOString(),
      updatedAt: new Date(Date.now() - Math.random() * 1000000000).toISOString(),
    };
  }),
  
  transactions: Array.from({ length: 50 }, (_, i) => {
    const isTradeOperation = i % 3 === 2;
    const createdAt = new Date(Date.now() - Math.random() * 10000000000).toISOString();
    const updatedAt = new Date(Date.now() - Math.random() * 1000000000).toISOString();
    const finishedAt = Math.random() > 0.3 ? new Date(Date.now() - Math.random() * 500000000).toISOString() : undefined;
    
    return {
      id: i + 1,
      userId: Math.floor(Math.random() * 20) + 1,
      accountType: ['spot', 'margin'][i % 2],
      type: ['deposit', 'withdrawal', 'trade'][i % 3],
      operationType: isTradeOperation ? ['buy', 'sell'][i % 2] : undefined,
      subType: isTradeOperation ? ['spot', 'margin'][i % 2] : null,
      amount: Math.round(Math.random() * 10000) / 100,
      currency: ['BTC', 'ETH', 'USDT', 'BNB'][i % 4],
      status: ['pending', 'completed', 'failed', 'cancelled'][i % 4],
      sourceAssetId: isTradeOperation ? (i % 10) + 1 : undefined,
      destAccountId: isTradeOperation ? (i % 5) + 1 : undefined,
      sourceAssetAmount0: isTradeOperation ? Math.floor(Math.random() * 100) : undefined,
      sourceAssetAmount1: isTradeOperation ? Math.floor(Math.random() * 1000000) : undefined,
      destAssetAmount0: isTradeOperation ? Math.floor(Math.random() * 100) : undefined,
      destAssetAmount1: isTradeOperation ? Math.floor(Math.random() * 1000000) : undefined,
      createdAt,
      updatedAt,
      finishedAt,
    };
  }),
  
  deposits: Array.from({ length: 40 }, (_, i) => {
    const createdAt = new Date(Date.now() - Math.random() * 10000000000).toISOString();
    const updatedAt = new Date(Date.now() - Math.random() * 1000000000).toISOString();
    const finishedAt = Math.random() > 0.3 ? new Date(Date.now() - Math.random() * 500000000).toISOString() : undefined;
    
    return {
      id: i + 1,
      userId: Math.floor(Math.random() * 20) + 1,
      direction: i % 2 as 0 | 1, // 0-вывод, 1-ввод
      assetId: (i % 4) + 1,
      blockchainId: (i % 3) + 1,
      walletId: `0x${Math.random().toString(16).substring(2, 42)}`,
      amount: Math.round(Math.random() * 10000) / 100,
      status: ['pending', 'processing', 'completed', 'failed'][i % 4],
      createdAt,
      updatedAt,
      finishedAt,
    };
  }),
  
  kycDocuments: Array.from({ length: 30 }, (_, i) => ({
    id: i + 1,
    userId: Math.floor(Math.random() * 20) + 1,
    documentName: ['Паспорт', 'Водительское удостоверение', 'ID карта', 'Подтверждение адреса'][i % 4],
    type: ['passport', 'driverLicense', 'idCard', 'proofOfAddress'][i % 4],
    documentType: ['image', 'video', 'pdf'][i % 3],
    fileUrl: `https://example.com/documents/doc${i + 1}.jpg`,
    status: ['pending', 'approved', 'rejected'][i % 3],
    comment: i % 3 === 2 ? 'Документ нечитаемый' : undefined,
    createdAt: new Date(Date.now() - Math.random() * 10000000000).toISOString(),
    updatedAt: new Date(Date.now() - Math.random() * 1000000000).toISOString(),
  })),
  
  userAssets: Array.from({ length: 60 }, (_, i) => ({
    userId: Math.floor(i / 3) + 1, // Каждый пользователь имеет несколько активов
    assetName: ['BTC', 'ETH', 'USDT', 'BNB', 'SOL', 'ADA'][i % 6],
    amount: Math.round(Math.random() * 100000) / 100,
    updatedAt: new Date(Date.now() - Math.random() * 1000000000).toISOString(),
  })),
  
  roles: [
    { id: 1, name: 'Администратор', status: 'active' },
    { id: 2, name: 'Модератор KYC', status: 'active' },
    { id: 3, name: 'Финансовый аналитик', status: 'active' },
    { id: 4, name: 'Служба поддержки', status: 'active' },
    { id: 5, name: 'Аудитор', status: 'inactive' },
  ],
  
  managers: Array.from({ length: 10 }, (_, i) => ({
    id: i + 1,
    roleId: (i % 5) + 1,
    roleName: ['Администратор', 'Модератор KYC', 'Финансовый аналитик', 'Служба поддержки', 'Аудитор'][i % 5],
    firstName: `Менеджер${i + 1}`,
    lastName: `Фамилия${i + 1}`,
    email: `manager${i + 1}@example.com`,
    status: i < 8 ? 'active' : 'suspended',
  })),
  
  auditLogs: Array.from({ length: 100 }, (_, i) => {
    const actions = [
      'user.create', 'user.update', 'user.suspend', 'user.activate',
      'kyc.approve', 'kyc.reject', 'kyc.request',
      'transaction.create', 'transaction.update', 'transaction.cancel',
      'deposit.approve', 'deposit.reject', 'withdrawal.approve', 'withdrawal.reject'
    ];
    const entityTypes = ['user', 'kyc', 'transaction', 'deposit', 'withdrawal'];
    const action = actions[i % actions.length];
    const entityType = entityTypes[Math.floor(i / 20)];
    
    let oldValue, newValue;
    if (action.includes('update')) {
      oldValue = JSON.stringify({ status: 'pending' });
      newValue = JSON.stringify({ status: 'completed' });
    } else if (action.includes('approve') || action.includes('reject')) {
      oldValue = JSON.stringify({ status: 'pending' });
      newValue = JSON.stringify({ status: action.includes('approve') ? 'approved' : 'rejected' });
    } else if (action.includes('suspend') || action.includes('activate')) {
      oldValue = JSON.stringify({ status: action.includes('suspend') ? 'active' : 'suspended' });
      newValue = JSON.stringify({ status: action.includes('suspend') ? 'suspended' : 'active' });
    }
    
    return {
      id: i + 1,
      userId: (i % 10) + 1, // ID менеджера, совершившего действие
      action,
      entityType,
      entityId: (i % 20) + 1,
      oldValue,
      newValue,
      createdAt: new Date(Date.now() - i * 3600000).toISOString(), // Каждая запись на час раньше предыдущей
    };
  }),
  
  analytics: {
    totalUsers: 1250,
    activeUsers: 780,
    totalTransactions: 15680,
    totalVolume: 4350000,
    dailyVolume: Array.from({ length: 14 }, (_, i) => ({
      date: new Date(Date.now() - (13 - i) * 86400000).toISOString().split('T')[0],
      volume: Math.round(Math.random() * 500000)
    })),
    transactionsByStatus: [
      { status: 'pending', count: 120 },
      { status: 'completed', count: 850 },
      { status: 'failed', count: 45 },
      { status: 'cancelled', count: 30 }
    ]
  }
};

// Интерцептор для обработки ошибок
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Обработка ошибки авторизации
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Интерцептор для моковых данных
if (USE_MOCK_DATA) {
  api.interceptors.request.use(async (config) => {
    const url = config.url || '';
    const method = config.method || 'get';
    
    // Искусственная задержка для имитации сетевого запроса
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Обработка запросов к пользователям
    if (url.includes('/users')) {
      if (url.match(/\/users\/\d+\/assets$/)) {
        // Получение активов пользователя
        const userId = parseInt(url.split('/')[2]);
        const userAssets = MOCK_DATA.userAssets.filter(a => a.userId === userId);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: userAssets,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else if (url.match(/\/users\/\d+$/)) {
        // Получение конкретного пользователя
        const id = parseInt(url.split('/').pop() || '0');
        const user = MOCK_DATA.users.find(u => u.id === id);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: user,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else if (url.match(/\/users\/\d+\/kyc-status$/)) {
        // Обновление KYC статуса пользователя
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { status } = JSON.parse(config.data);
          const user = MOCK_DATA.users.find(u => u.id === id);
          if (user) {
            user.kycStatus = status;
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (url.match(/\/users\/\d+\/status$/)) {
        // Обновление статуса пользователя
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { status } = JSON.parse(config.data);
          const user = MOCK_DATA.users.find(u => u.id === id);
          if (user) {
            user.status = status;
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else {
        // Получение списка пользователей с пагинацией
        const params = config.params || {};
        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const end = start + limit;
        
        // Применение фильтров
        let filteredUsers = [...MOCK_DATA.users];
        
        if (params.status) {
          filteredUsers = filteredUsers.filter(u => u.status === params.status);
        }
        
        if (params.kycStatus) {
          filteredUsers = filteredUsers.filter(u => u.kycStatus === params.kycStatus);
        }
        
        if (params.search) {
          const search = params.search.toLowerCase();
          filteredUsers = filteredUsers.filter(u => 
            u.firstName.toLowerCase().includes(search) ||
            u.lastName.toLowerCase().includes(search) ||
            u.email.toLowerCase().includes(search) ||
            u.phone.includes(search) ||
            u.id.toString() === search
          );
        }
        
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: {
              data: filteredUsers.slice(start, end),
              total: filteredUsers.length
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Обработка запросов к транзакциям
    if (url.includes('/transactions') && !url.includes('/analytics')) {
      if (url.match(/\/transactions\/\d+\/status$/)) {
        // Обновление статуса транзакции
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { status } = JSON.parse(config.data);
          const transaction = MOCK_DATA.transactions.find(t => t.id === id);
          if (transaction) {
            transaction.status = status;
            transaction.updatedAt = new Date().toISOString();
            if (status === 'completed' || status === 'failed' || status === 'cancelled') {
              transaction.finishedAt = new Date().toISOString();
            }
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (url.match(/\/transactions\/\d+$/)) {
        // Получение конкретной транзакции
        const id = parseInt(url.split('/').pop() || '0');
        const transaction = MOCK_DATA.transactions.find(t => t.id === id);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: transaction,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else {
        // Получение списка транзакций с пагинацией
        const params = config.params || {};
        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const end = start + limit;
        
        // Применение фильтров
        let filteredTransactions = [...MOCK_DATA.transactions];
        
        if (params.type) {
          filteredTransactions = filteredTransactions.filter(t => t.type === params.type);
        }
        
        if (params.status) {
          filteredTransactions = filteredTransactions.filter(t => t.status === params.status);
        }
        
        if (params.userId) {
          filteredTransactions = filteredTransactions.filter(t => t.userId === parseInt(params.userId));
        }
        
        if (params.startDate) {
          const startDate = new Date(params.startDate).getTime();
          filteredTransactions = filteredTransactions.filter(t => new Date(t.createdAt).getTime() >= startDate);
        }
        
        if (params.endDate) {
          const endDate = new Date(params.endDate).getTime();
          filteredTransactions = filteredTransactions.filter(t => new Date(t.createdAt).getTime() <= endDate);
        }
        
        if (params.search) {
          const search = params.search.toLowerCase();
          filteredTransactions = filteredTransactions.filter(t => 
            t.id.toString() === search ||
            t.userId.toString() === search ||
            t.currency.toLowerCase().includes(search)
          );
        }
        
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: {
              data: filteredTransactions.slice(start, end),
              total: filteredTransactions.length
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Обработка запросов к вводам-выводам
    if (url.includes('/deposits')) {
      if (url.match(/\/deposits\/\d+\/status$/)) {
        // Обновление статуса ввода-вывода
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { status } = JSON.parse(config.data);
          const deposit = MOCK_DATA.deposits.find(d => d.id === id);
          if (deposit) {
            deposit.status = status;
            deposit.updatedAt = new Date().toISOString();
            if (status === 'completed' || status === 'failed') {
              deposit.finishedAt = new Date().toISOString();
            }
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (url.match(/\/deposits\/\d+$/)) {
        // Получение конкретного ввода-вывода
        const id = parseInt(url.split('/').pop() || '0');
        const deposit = MOCK_DATA.deposits.find(d => d.id === id);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: deposit,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else {
        // Получение списка вводов-выводов с пагинацией
        const params = config.params || {};
        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const end = start + limit;
        
        // Применение фильтров
        let filteredDeposits = [...MOCK_DATA.deposits];
        
        if (params.direction !== undefined) {
          filteredDeposits = filteredDeposits.filter(d => d.direction === parseInt(params.direction));
        }
        
        if (params.status) {
          filteredDeposits = filteredDeposits.filter(d => d.status === params.status);
        }
        
        if (params.userId) {
          filteredDeposits = filteredDeposits.filter(d => d.userId === parseInt(params.userId));
        }
        
        if (params.startDate) {
          const startDate = new Date(params.startDate).getTime();
          filteredDeposits = filteredDeposits.filter(d => new Date(d.createdAt).getTime() >= startDate);
        }
        
        if (params.endDate) {
          const endDate = new Date(params.endDate).getTime();
          filteredDeposits = filteredDeposits.filter(d => new Date(d.createdAt).getTime() <= endDate);
        }
        
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: {
              data: filteredDeposits.slice(start, end),
              total: filteredDeposits.length
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Обработка запросов к KYC документам
    if (url.includes('/kyc-documents')) {
      if (url.match(/\/kyc-documents\/\d+\/status$/)) {
        // Обновление статуса KYC документа
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { status, comment } = JSON.parse(config.data);
          const document = MOCK_DATA.kycDocuments.find(d => d.id === id);
          if (document) {
            document.status = status;
            if (comment) document.comment = comment;
            document.updatedAt = new Date().toISOString();
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (url.match(/\/kyc-documents\/\d+$/)) {
        // Получение конкретного документа
        const id = parseInt(url.split('/').pop() || '0');
        const document = MOCK_DATA.kycDocuments.find(d => d.id === id);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: document,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else {
        // Получение списка документов с пагинацией
        const params = config.params || {};
        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const end = start + limit;
        
        // Применение фильтров
        let filteredDocuments = [...MOCK_DATA.kycDocuments];
        
        if (params.status) {
          filteredDocuments = filteredDocuments.filter(d => d.status === params.status);
        }
        
        if (params.type) {
          filteredDocuments = filteredDocuments.filter(d => d.type === params.type);
        }
        
        if (params.userId) {
          filteredDocuments = filteredDocuments.filter(d => d.userId === parseInt(params.userId));
        }
        
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: {
              data: filteredDocuments.slice(start, end),
              total: filteredDocuments.length
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Обработка запросов к ролям
    if (url.includes('/roles')) {
      if (method === 'get') {
        if (url.match(/\/roles\/\d+$/)) {
          // Получение конкретной роли
          const id = parseInt(url.split('/').pop() || '0');
          const role = MOCK_DATA.roles.find(r => r.id === id);
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: role,
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        } else {
          // Получение списка ролей
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: MOCK_DATA.roles,
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (method === 'post') {
        // Создание новой роли
        const roleData = JSON.parse(config.data);
        const newRole = {
          id: Math.max(...MOCK_DATA.roles.map(r => r.id)) + 1,
          ...roleData
        };
        MOCK_DATA.roles.push(newRole);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: newRole,
            status: 201,
            statusText: 'Created',
            headers: {},
            config
          })
        };
      } else if (method === 'patch' && url.match(/\/roles\/\d+$/)) {
        // Обновление роли
        const id = parseInt(url.split('/').pop() || '0');
        const roleData = JSON.parse(config.data);
        const role = MOCK_DATA.roles.find(r => r.id === id);
        if (role) {
          Object.assign(role, roleData);
        }
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: role,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else if (method === 'delete' && url.match(/\/roles\/\d+$/)) {
        // Удаление роли
        const id = parseInt(url.split('/').pop() || '0');
        const index = MOCK_DATA.roles.findIndex(r => r.id === id);
        if (index !== -1) {
          MOCK_DATA.roles.splice(index, 1);
        }
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: { success: true },
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Обработка запросов к менеджерам
    if (url.includes('/managers')) {
      if (url.match(/\/managers\/\d+\/status$/)) {
        // Обновление статуса менеджера
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { status } = JSON.parse(config.data);
          const manager = MOCK_DATA.managers.find(m => m.id === id);
          if (manager) {
            manager.status = status;
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (url.match(/\/managers\/\d+\/role$/)) {
        // Обновление роли менеджера
        if (method === 'patch') {
          const id = parseInt(url.split('/')[2]);
          const { roleId } = JSON.parse(config.data);
          const manager = MOCK_DATA.managers.find(m => m.id === id);
          const role = MOCK_DATA.roles.find(r => r.id === roleId);
          if (manager && role) {
            manager.roleId = roleId;
            manager.roleName = role.name;
          }
          return {
            ...config,
            adapter: () => Promise.resolve({
              data: { success: true },
              status: 200,
              statusText: 'OK',
              headers: {},
              config
            })
          };
        }
      } else if (url.match(/\/managers\/\d+$/)) {
        // Получение конкретного менеджера
        const id = parseInt(url.split('/').pop() || '0');
        const manager = MOCK_DATA.managers.find(m => m.id === id);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: manager,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else if (method === 'post') {
        // Создание нового менеджера
        const managerData = JSON.parse(config.data);
        const role = MOCK_DATA.roles.find(r => r.id === managerData.roleId);
        const newManager = {
          id: Math.max(...MOCK_DATA.managers.map(m => m.id)) + 1,
          ...managerData,
          roleName: role ? role.name : undefined
        };
        MOCK_DATA.managers.push(newManager);
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: newManager,
            status: 201,
            statusText: 'Created',
            headers: {},
            config
          })
        };
      } else if (method === 'patch' && url.match(/\/managers\/\d+$/)) {
        // Обновление менеджера
        const id = parseInt(url.split('/').pop() || '0');
        const managerData = JSON.parse(config.data);
        const manager = MOCK_DATA.managers.find(m => m.id === id);
        if (manager) {
          Object.assign(manager, managerData);
          if (managerData.roleId) {
            const role = MOCK_DATA.roles.find(r => r.id === managerData.roleId);
            if (role) {
              manager.roleName = role.name;
            }
          }
        }
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: manager,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else {
        // Получение списка менеджеров с пагинацией
        const params = config.params || {};
        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const end = start + limit;
        
        // Применение фильтров
        let filteredManagers = [...MOCK_DATA.managers];
        
        if (params.status) {
          filteredManagers = filteredManagers.filter(m => m.status === params.status);
        }
        
        if (params.roleId) {
          filteredManagers = filteredManagers.filter(m => m.roleId === parseInt(params.roleId));
        }
        
        if (params.search) {
          const search = params.search.toLowerCase();
          filteredManagers = filteredManagers.filter(m => 
            m.firstName.toLowerCase().includes(search) ||
            m.lastName.toLowerCase().includes(search) ||
            m.email.toLowerCase().includes(search) ||
            (m.roleName && m.roleName.toLowerCase().includes(search))
          );
        }
        
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: {
              data: filteredManagers.slice(start, end),
              total: filteredManagers.length
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Обработка запросов к аудиту
    if (url.includes('/audit')) {
      const params = config.params || {};
      const page = params.page || 1;
      const limit = params.limit || 10;
      const start = (page - 1) * limit;
      const end = start + limit;
      
      // Применение фильтров
      let filteredLogs = [...MOCK_DATA.auditLogs];
      
      if (params.userId) {
        filteredLogs = filteredLogs.filter(log => log.userId === parseInt(params.userId));
      }
      
      if (params.action) {
        filteredLogs = filteredLogs.filter(log => log.action === params.action);
      }
      
      if (params.entityType) {
        filteredLogs = filteredLogs.filter(log => log.entityType === params.entityType);
      }
      
      if (params.entityId) {
        filteredLogs = filteredLogs.filter(log => log.entityId === parseInt(params.entityId));
      }
      
      if (params.startDate) {
        const startDate = new Date(params.startDate).getTime();
        filteredLogs = filteredLogs.filter(log => new Date(log.createdAt).getTime() >= startDate);
      }
      
      if (params.endDate) {
        const endDate = new Date(params.endDate).getTime();
        filteredLogs = filteredLogs.filter(log => new Date(log.createdAt).getTime() <= endDate);
      }
      
      // Сортировка по времени создания (от новых к старым)
      filteredLogs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      return {
        ...config,
        adapter: () => Promise.resolve({
          data: {
            data: filteredLogs.slice(start, end),
            total: filteredLogs.length
          },
          status: 200,
          statusText: 'OK',
          headers: {},
          config
        })
      };
    }
    
    // Обработка запросов к аналитике
    if (url.includes('/analytics')) {
      console.log('Intercepting analytics request:', url); // Отладочный вывод
      
      if (url.includes('/dashboard')) {
        console.log('Mock analytics data:', MOCK_DATA.analytics); // Отладочный вывод
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: MOCK_DATA.analytics,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else if (url.includes('/volume')) {
        const params = config.params || {};
        const { startDate, endDate } = params;
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: MOCK_DATA.analytics.dailyVolume,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      } else if (url.includes('/transactions')) {
        const params = config.params || {};
        const { startDate, endDate } = params;
        return {
          ...config,
          adapter: () => Promise.resolve({
            data: MOCK_DATA.analytics.transactionsByStatus,
            status: 200,
            statusText: 'OK',
            headers: {},
            config
          })
        };
      }
    }
    
    // Если запрос не обработан, пропускаем его дальше
    return config;
  }, error => Promise.reject(error));
}

// Типы данных для API

// Пользователь
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  secondName?: string; // Отчество
  email: string;
  phone: string;
  status: 'active' | 'suspended' | 'blocked'; // Статус активности пользователя
  kycStatus: 'pending' | 'approved' | 'rejected';
  accountAge: string; // Возраст аккаунта в формате "x мес. y дней z часов"
  spotAccountTurnoverDaily: number;
  spotAccountTurnoverMonthly: number;
  marginAccountTurnoverDaily: number;
  marginAccountTurnoverMonthly: number;
  createdAt: string;
  updatedAt: string;
}

// Операция
export interface Transaction {
  id: number;
  userId: number;
  accountType?: 'spot' | 'margin'; // Тип аккаунта
  type: 'deposit' | 'withdrawal' | 'trade';
  operationType?: 'buy' | 'sell'; // Тип операции для торговых операций
  subType: 'spot' | 'margin' | null;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  sourceAssetId?: number; // ID исходного актива
  destAccountId?: number; // ID целевого аккаунта
  sourceAssetAmount0?: number; // Исходная сумма актива (целая часть)
  sourceAssetAmount1?: number; // Исходная сумма актива (дробная часть)
  destAssetAmount0?: number; // Целевая сумма актива (целая часть)
  destAssetAmount1?: number; // Целевая сумма актива (дробная часть)
  createdAt: string; // Время создания операции
  updatedAt: string; // Время обновления операции
  finishedAt?: string; // Время завершения операции
}

// Ввод-вывод средств
export interface Deposit {
  id: number;
  userId: number;
  direction: 0 | 1; // 0-вывод, 1-ввод
  assetId: number;
  blockchainId: number;
  walletId: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string; // Время запроса операции
  updatedAt: string; // Время обновления операции
  finishedAt?: string; // Время завершения операции
}

// KYC документ
export interface KycDocument {
  id: number;
  userId: number;
  documentName: string; // Название документа
  type: 'passport' | 'driverLicense' | 'idCard' | 'proofOfAddress';
  documentType: 'image' | 'video' | 'pdf'; // Тип файла документа
  fileUrl: string;
  fileBlob?: Blob; // Содержимое файла (для фронтенда)
  status: 'pending' | 'approved' | 'rejected';
  comment?: string;
  createdAt: string;
  updatedAt: string;
}

// Баланс актива пользователя
export interface UserAsset {
  userId: number;
  assetName: string; // Название актива
  amount: number; // Количество
  updatedAt: string; // Время последней операции
}

// Роль внутреннего пользователя
export interface Role {
  id: number;
  name: string;
  status: 'active' | 'inactive';
}

// Внутренний пользователь (менеджер)
export interface Manager {
  id: number;
  roleId: number;
  roleName?: string; // Название роли (для отображения)
  firstName: string;
  lastName: string;
  email: string;
  status: 'active' | 'suspended';
}

// Запись аудита
export interface AuditLog {
  id: number;
  userId: number;
  action: string;
  entityType: string;
  entityId: number;
  oldValue?: string;
  newValue?: string;
  createdAt: string;
}

// Аналитические данные
export interface AnalyticsData {
  totalUsers: number;
  activeUsers: number;
  totalTransactions: number;
  totalVolume: number;
  dailyVolume: {
    date: string;
    volume: number;
  }[];
  transactionsByStatus: {
    status: string;
    count: number;
  }[];
}

// API методы для работы с пользователями
export const userApi = {
  getUsers: (page = 1, limit = 10, filter = {}) => 
    api.get<{ data: User[], total: number }>('/users', { params: { page, limit, ...filter } }),
  
  getUserById: (id: number) => 
    api.get<User>(`/users/${id}`),
  
  updateKycStatus: (id: number, status: 'pending' | 'approved' | 'rejected') =>
    api.patch(`/users/${id}/kyc-status`, { status }),
  
  updateUserStatus: (id: number, status: 'active' | 'suspended' | 'blocked') =>
    api.patch(`/users/${id}/status`, { status }),
};

// API методы для работы с транзакциями
export const transactionApi = {
  getTransactions: (page = 1, limit = 10, filter = {}) =>
    api.get<{ data: Transaction[], total: number }>('/transactions', { params: { page, limit, ...filter } }),
  
  getTransactionById: (id: number) =>
    api.get<Transaction>(`/transactions/${id}`),
  
  updateTransactionStatus: (id: number, status: 'pending' | 'completed' | 'failed' | 'cancelled') =>
    api.patch(`/transactions/${id}/status`, { status }),
};

// API методы для работы с KYC документами
export const kycApi = {
  getDocuments: (page = 1, limit = 10, filter = {}) =>
    api.get<{ data: KycDocument[], total: number }>('/kyc-documents', { params: { page, limit, ...filter } }),
  
  getDocumentById: (id: number) =>
    api.get<KycDocument>(`/kyc-documents/${id}`),
  
  updateDocumentStatus: (id: number, status: 'pending' | 'approved' | 'rejected', comment?: string) =>
    api.patch(`/kyc-documents/${id}/status`, { status, comment }),
};

// API методы для получения аналитики
export const analyticsApi = {
  getDashboardData: async () => {
    console.log('Calling getDashboardData API'); // Отладочный вывод
    try {
      const response = await api.get<AnalyticsData>('/analytics/dashboard');
      console.log('API response:', response); // Отладочный вывод для всего ответа
      console.log('API response data:', response.data); // Отладочный вывод для данных
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },
  
  getVolumeByPeriod: (startDate: string, endDate: string) =>
    api.get<{ date: string, volume: number }[]>('/analytics/volume', { params: { startDate, endDate } }),
  
  getTransactionStats: (startDate: string, endDate: string) =>
    api.get<{ status: string, count: number }[]>('/analytics/transactions', { params: { startDate, endDate } }),
};

// API методы для работы с вводами-выводами
export const depositApi = {
  getDeposits: (page = 1, limit = 10, filter = {}) =>
    api.get<{ data: Deposit[], total: number }>('/deposits', { params: { page, limit, ...filter } }),
  
  getDepositById: (id: number) =>
    api.get<Deposit>(`/deposits/${id}`),
  
  updateDepositStatus: (id: number, status: 'pending' | 'processing' | 'completed' | 'failed') =>
    api.patch(`/deposits/${id}/status`, { status }),
};

// API методы для работы с балансами пользователей
export const userAssetApi = {
  getUserAssets: (userId: number) =>
    api.get<UserAsset[]>(`/users/${userId}/assets`),
};

// API методы для работы с ролями
export const roleApi = {
  getRoles: () =>
    api.get<Role[]>('/roles'),
  
  getRoleById: (id: number) =>
    api.get<Role>(`/roles/${id}`),
  
  createRole: (data: Omit<Role, 'id'>) =>
    api.post('/roles', data),
  
  updateRole: (id: number, data: Partial<Role>) =>
    api.patch(`/roles/${id}`, data),
  
  deleteRole: (id: number) =>
    api.delete(`/roles/${id}`),
};

// API методы для работы с менеджерами
export const managerApi = {
  getManagers: (page = 1, limit = 10, filter = {}) =>
    api.get<{ data: Manager[], total: number }>('/managers', { params: { page, limit, ...filter } }),
  
  getManagerById: (id: number) =>
    api.get<Manager>(`/managers/${id}`),
  
  createManager: (data: Omit<Manager, 'id'>) =>
    api.post('/managers', data),
  
  updateManager: (id: number, data: Partial<Manager>) =>
    api.patch(`/managers/${id}`, data),
  
  updateManagerStatus: (id: number, status: 'active' | 'suspended') =>
    api.patch(`/managers/${id}/status`, { status }),
  
  updateManagerRole: (id: number, roleId: number) =>
    api.patch(`/managers/${id}/role`, { roleId }),
};

// API методы для работы с аудитом
export const auditApi = {
  getAuditLogs: (page = 1, limit = 10, filter = {}) =>
    api.get<{ data: AuditLog[], total: number }>('/audit', { params: { page, limit, ...filter } }),
}; 